/**
 * Free shipping progress bar for the cart drawer.
 *
 * Loaded as a separate asset (outside the <cart-drawer> element) because the
 * drawer re-renders itself with innerHTML, and scripts injected that way never
 * execute. Loading it here means the custom element is always registered before
 * the drawer injects its markup.
 */

if (!customElements.get('cart-drawer-free-shipping')) {
  class CartDrawerFreeShipping extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold);
      this.message = this.querySelector('.cd-free-shipping__message');
      this.track = this.querySelector('.cd-free-shipping__track');
      this.fill = this.querySelector('.cd-free-shipping__fill');

      this.cartEvents = [
        'on:cart:add',
        'on:cart:change',
        'on:cart:updated',
        'on:line-item:change',
        'cart:updated',
        'cart:refresh',
        'dispatch:cart-drawer:refresh'
      ];

      this.refresh();

      // Refresh whenever the cart changes. The theme fires different events
      // depending on how the cart was updated, so listen to all of them.
      this.refreshHandler = this.refresh.bind(this);
      this.cartEvents.forEach((evt) => document.addEventListener(evt, this.refreshHandler));

      // Fallback: watch the drawer subtotal for changes, in case the theme
      // updates the cart without firing any of the events above.
      const subtotal = document.querySelector('#cart-drawer .cart-subtotal__value');
      if (subtotal && 'MutationObserver' in window) {
        this.observer = new MutationObserver(this.refreshHandler);
        this.observer.observe(subtotal, { childList: true, characterData: true, subtree: true });
      }
    }

    disconnectedCallback() {
      if (this.cartEvents) {
        this.cartEvents.forEach((evt) => document.removeEventListener(evt, this.refreshHandler));
      }
      if (this.observer) this.observer.disconnect();
    }

    refresh() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => this.update(cart.total_price))
        .catch(() => {});
    }

    update(total) {
      if (!this.message || !this.track || !this.fill) return;

      const remaining = Math.max(this.threshold - total, 0);
      const progress = this.threshold > 0 ? Math.min((total / this.threshold) * 100, 100) : 100;
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD'
      });

      this.track.setAttribute('aria-valuenow', String(Math.round(progress)));
      this.fill.style.width = progress + '%';

      if (remaining > 0) {
        this.classList.remove('cd-free-shipping--complete');
        this.message.innerHTML = this.dataset.progressMessage.replace(
          '[amount]',
          '<strong>' + formatter.format(remaining / 100) + '</strong>'
        );
      } else {
        this.classList.add('cd-free-shipping--complete');
      }
    }
  }

  customElements.define('cart-drawer-free-shipping', CartDrawerFreeShipping);
}
